import React, { useState, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Baby, CheckCircle, UserPlus, Upload, X, FileText, File } from 'lucide-react';
import { toast } from 'sonner';

import API from '@/config/api';
// API imported from config;

const EmployeeApplicationPage = () => {
  const [searchParams] = useSearchParams();
  const centerId = searchParams.get('center') || '';
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const resumeInputRef = useRef(null);
  const certInputRef = useRef(null);
  const [centerSettings, setCenterSettings] = useState({ center_name: '', logo_url: null });
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    const params = centerId ? `?center_id=${centerId}` : '';
    axios.get(`${API}/settings/public${params}`)
      .then(res => setCenterSettings(res.data))
      .catch(() => {});
  }, []);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience_years: 0,
    certifications: '',
    education: '',
    availability: '',
    references: '',
    cover_letter: '',
    resume_filename: null,
    resume_data: null,
    certification_files: []
  });

  const handleFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data:*/*;base64, prefix
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF or Word document');
      return;
    }

    try {
      const base64 = await handleFileToBase64(file);
      setFormData({
        ...formData,
        resume_filename: file.name,
        resume_data: base64
      });
      toast.success('Resume uploaded');
    } catch (error) {
      toast.error('Failed to upload file');
    }
  };

  const handleCertificationUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const newCertFiles = [];
    
    for (const file of files) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 5MB)`);
        continue;
      }

      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name} - Invalid file type`);
        continue;
      }

      try {
        const base64 = await handleFileToBase64(file);
        newCertFiles.push({
          filename: file.name,
          data: base64
        });
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (newCertFiles.length > 0) {
      setFormData({
        ...formData,
        certification_files: [...formData.certification_files, ...newCertFiles]
      });
      toast.success(`${newCertFiles.length} certification file(s) uploaded`);
    }
  };

  const removeResume = () => {
    setFormData({
      ...formData,
      resume_filename: null,
      resume_data: null
    });
  };

  const removeCertification = (index) => {
    setFormData({
      ...formData,
      certification_files: formData.certification_files.filter((_, i) => i !== index)
    });
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('Full name is required');
      return false;
    }
    if (!formData.email.trim()) {
      toast.error('Email is required');
      return false;
    }
    if (!formData.phone.trim()) {
      toast.error('Phone number is required');
      return false;
    }
    if (!formData.position) {
      toast.error('Position is required');
      return false;
    }
    if (!formData.availability.trim()) {
      toast.error('Availability is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/applications/employee`, { ...formData, ...(centerId && { center_id: centerId }) });
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename.toLowerCase().split('.').pop();
    if (ext === 'pdf') return <FileText className="w-5 h-5 text-red-500" />;
    if (['doc', 'docx'].includes(ext)) return <FileText className="w-5 h-5 text-blue-500" />;
    if (['jpg', 'jpeg', 'png'].includes(ext)) return <File className="w-5 h-5 text-green-500" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="card-clay max-w-md w-full text-center">
          <CardContent className="p-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="font-heading font-bold text-2xl mb-2">Application Submitted!</h2>
            <p className="text-muted-foreground mb-6">
              Thank you for your interest in joining our team. We will review your application and contact you soon.
            </p>
            <Link to="/">
              <Button>Back to Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4" data-testid="employee-application-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            {centerSettings.logo_url && !logoError ? (
              <img 
                src={centerSettings.logo_url.startsWith('http') ? centerSettings.logo_url : `${API}${centerSettings.logo_url.replace('/api', '')}`} 
                alt={centerSettings.center_name} 
                className="w-12 h-12 rounded-xl object-cover" 
                onError={() => setLogoError(true)}
              />
            ) : (
              <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                <Baby className="w-7 h-7 text-primary-foreground" />
              </div>
            )}
            <span className="font-heading font-bold text-2xl" data-testid="center-name-header">{centerSettings.center_name || 'Child Care Center'}</span>
          </Link>
          <h1 className="font-heading font-bold text-3xl mb-2">Join Our Team</h1>
          <p className="text-muted-foreground">Apply to become a child care provider at our facility</p>
        </div>

        <Card className="card-clay">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" />
              Employee Application
            </CardTitle>
            <CardDescription>
              Please fill out all required fields. We'll review your application and contact you.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Personal Information</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      data-testid="employee-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      data-testid="employee-email-input"
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required
                      data-testid="employee-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="position">Position Applying For *</Label>
                    <Select value={formData.position} onValueChange={(value) => setFormData({...formData, position: value})}>
                      <SelectTrigger data-testid="employee-position-select">
                        <SelectValue placeholder="Select position" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Lead Teacher">Lead Teacher</SelectItem>
                        <SelectItem value="Assistant Teacher">Assistant Teacher</SelectItem>
                        <SelectItem value="Aide">Aide</SelectItem>
                        <SelectItem value="Substitute">Substitute</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Experience & Qualifications */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Experience & Qualifications</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="experience">Years of Experience *</Label>
                    <Input
                      id="experience"
                      type="number"
                      min="0"
                      value={formData.experience_years}
                      onChange={(e) => setFormData({...formData, experience_years: parseInt(e.target.value) || 0})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="education">Education Level</Label>
                    <Input
                      id="education"
                      value={formData.education}
                      onChange={(e) => setFormData({...formData, education: e.target.value})}
                      placeholder="e.g., Associate's in Early Childhood"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="certifications">Certifications (CPR, First Aid, CDA, etc.)</Label>
                  <Input
                    id="certifications"
                    value={formData.certifications}
                    onChange={(e) => setFormData({...formData, certifications: e.target.value})}
                    placeholder="List your certifications"
                  />
                </div>
              </div>

              {/* File Uploads */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Document Uploads</h3>
                
                {/* Resume Upload */}
                <div className="space-y-2">
                  <Label>Resume / CV</Label>
                  <input
                    ref={resumeInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleResumeUpload}
                    className="hidden"
                  />
                  {formData.resume_filename ? (
                    <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div className="flex items-center gap-3">
                        {getFileIcon(formData.resume_filename)}
                        <span className="text-sm font-medium">{formData.resume_filename}</span>
                      </div>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={removeResume}
                        className="text-destructive hover:text-destructive"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div 
                      className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                      onClick={() => resumeInputRef.current?.click()}
                    >
                      <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        Click to upload your resume
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        PDF or Word document (max 5MB)
                      </p>
                    </div>
                  )}
                </div>

                {/* Certification Uploads */}
                <div className="space-y-2">
                  <Label>Certification Documents (Optional)</Label>
                  <input
                    ref={certInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    multiple
                    onChange={handleCertificationUpload}
                    className="hidden"
                  />
                  
                  {formData.certification_files.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {formData.certification_files.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                          <div className="flex items-center gap-3">
                            {getFileIcon(file.filename)}
                            <span className="text-sm font-medium">{file.filename}</span>
                          </div>
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm"
                            onClick={() => removeCertification(index)}
                            className="text-destructive hover:text-destructive"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div 
                    className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => certInputRef.current?.click()}
                  >
                    <Upload className="w-6 h-6 mx-auto mb-1 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Upload CPR card, First Aid cert, CDA certificate, etc.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      PDF, Word, or images (max 5MB each)
                    </p>
                  </div>
                </div>
              </div>

              {/* Availability & References */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Availability & References</h3>
                <div className="space-y-2">
                  <Label htmlFor="availability">Availability</Label>
                  <Input
                    id="availability"
                    value={formData.availability}
                    onChange={(e) => setFormData({...formData, availability: e.target.value})}
                    placeholder="e.g., Monday-Friday, Full-time"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="references">References</Label>
                  <Textarea
                    id="references"
                    value={formData.references}
                    onChange={(e) => setFormData({...formData, references: e.target.value})}
                    placeholder="Please provide 2-3 professional references with contact information"
                    rows={3}
                  />
                </div>
              </div>

              {/* Cover Letter */}
              <div className="space-y-2">
                <Label htmlFor="cover_letter">Why do you want to work with us?</Label>
                <Textarea
                  id="cover_letter"
                  value={formData.cover_letter}
                  onChange={(e) => setFormData({...formData, cover_letter: e.target.value})}
                  placeholder="Tell us about yourself and why you're interested in this position..."
                  rows={5}
                />
              </div>

              <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="submit-employee-app-btn">
                {loading ? 'Submitting...' : 'Submit Application'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmployeeApplicationPage;
